import React, { useState } from 'react';
import { useCurrency } from '../../context/CurrencyContext';
import './VerificationBadgeModal.css';

const VerificationBadgeModal = ({ isOpen, onClose, badge, onSubmit }) => {
  const { formatPrice } = useCurrency();
  const [formData, setFormData] = useState({
    businessRegistration: null,
    tradeVATCertificate: null,
    companyOwnerID: null,
    businessLicense: null,
    companyAddress: null
  });
  
  const [uploadStatus, setUploadStatus] = useState({
    businessRegistration: 'pending',
    tradeVATCertificate: 'pending',
    companyOwnerID: 'pending',
    businessLicense: 'pending',
    companyAddress: 'pending'
  });

  if (!isOpen || !badge) return null;

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, [field]: file }));
      setUploadStatus(prev => ({ ...prev, [field]: 'uploaded' }));
    }
  };

  const handleRemoveFile = (field) => {
    setFormData(prev => ({ ...prev, [field]: null }));
    setUploadStatus(prev => ({ ...prev, [field]: 'pending' }));
  };

  const handleSubmit = () => {
    // Check required documents based on badge type
    const requiredDocs = ['businessRegistration'];
    
    if (badge.name === 'inspected' || badge.name === 'certified') {
      requiredDocs.push('tradeVATCertificate', 'companyOwnerID', 'businessLicense', 'companyAddress');
    }
    
    const missingDocs = requiredDocs.filter(doc => !formData[doc]);
    
    if (missingDocs.length > 0) {
      alert('Please upload all required documents');
      return;
    }
    
    onSubmit(formData);
  };

  const getStatusIcon = (status) => {
    if (status === 'uploaded') {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="#009D0A"/>
        </svg>
      );
    }
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="9" stroke="#E6EBEF" strokeWidth="2"/>
      </svg>
    );
  };

  const documentRequirements = [
    {
      id: 'businessRegistration',
      label: 'Business Registration Certificate',
      description: 'Upload your company registration document',
      required: true,
      icon: '📄'
    },
    {
      id: 'tradeVATCertificate',
      label: 'Trade or VAT Certificate',
      description: 'Upload your trade license or VAT registration',
      required: badge.name !== 'verified',
      icon: '📋'
    },
    {
      id: 'companyOwnerID',
      label: 'Company Owner/Director ID Proof (Passport, National ID, or Driver\'s License)',
      description: 'Upload ID proof of company owner or director',
      required: badge.name !== 'verified',
      icon: '🪪'
    },
    {
      id: 'businessLicense',
      label: 'Business License or Operating Permit',
      description: 'Upload your business operating license',
      required: badge.name !== 'verified',
      icon: '📜'
    },
    {
      id: 'companyAddress',
      label: 'Proof of Company Address (Utility Bill or Lease Agreement)',
      description: 'Upload proof of your company address',
      required: badge.name !== 'verified',
      icon: '🏢'
    }
  ];

  return (
    <div className="verification-modal-overlay" onClick={onClose}>
      <div className="verification-modal" onClick={(e) => e.stopPropagation()}>
        <div className="verification-modal-header">
          <h2>Verification Badges (Optional Add-ons)</h2>
          <p>Boost your credibility and stand out - choose the badge level that matches your trust goals.</p>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#1B1B1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="verification-modal-content">
          <div className="badge-selection-info">
            <div className="selected-badge-icon">
              {badge.name === 'verified' && (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M31.7616 14.4591C31.2314 13.905 30.683 13.3341 30.4762 12.832C30.285 12.3722 30.2738 11.61 30.2625 10.8717C30.2414 9.49922 30.2189 7.94391 29.1375 6.8625C28.0561 5.78109 26.5008 5.75859 25.1283 5.7375C24.39 5.72625 23.6278 5.715 23.168 5.52375C22.6673 5.31703 22.095 4.76859 21.5409 4.23844C20.5706 3.30609 19.4681 2.25 18 2.25C16.5319 2.25 15.4308 3.30609 14.4591 4.23844C13.905 4.76859 13.3341 5.31703 12.832 5.52375C12.375 5.715 11.61 5.72625 10.8717 5.7375C9.49922 5.75859 7.94391 5.78109 6.8625 6.8625C5.78109 7.94391 5.76562 9.49922 5.7375 10.8717C5.72625 11.61 5.715 12.3722 5.52375 12.832C5.31703 13.3327 4.76859 13.905 4.23844 14.4591C3.30609 15.4294 2.25 16.5319 2.25 18C2.25 19.4681 3.30609 20.5692 4.23844 21.5409C4.76859 22.095 5.31703 22.6659 5.52375 23.168C5.715 23.6278 5.72625 24.39 5.7375 25.1283C5.75859 26.5008 5.78109 28.0561 6.8625 29.1375C7.94391 30.2189 9.49922 30.2414 10.8717 30.2625C11.61 30.2738 12.3722 30.285 12.832 30.4762C13.3327 30.683 13.905 31.2314 14.4591 31.7616C15.4294 32.6939 16.5319 33.75 18 33.75C19.4681 33.75 20.5692 32.6939 21.5409 31.7616C22.095 31.2314 22.6659 30.683 23.168 30.4762C23.6278 30.285 24.39 30.2738 25.1283 30.2625C26.5008 30.2414 28.0561 30.2189 29.1375 29.1375C30.2189 28.0561 30.2414 26.5008 30.2625 25.1283C30.2738 24.39 30.285 23.6278 30.4762 23.168C30.683 22.6673 31.2314 22.095 31.7616 21.5409C32.6939 20.5706 33.75 19.4681 33.75 18C33.75 16.5319 32.6939 15.4308 31.7616 14.4591ZM24.4209 15.4209L16.5459 23.2959C16.4415 23.4005 16.3174 23.4835 16.1808 23.5401C16.0442 23.5967 15.8978 23.6259 15.75 23.6259C15.6022 23.6259 15.4558 23.5967 15.3192 23.5401C15.1826 23.4835 15.0585 23.4005 14.9541 23.2959L11.5791 19.9209C11.368 19.7098 11.2494 19.4235 11.2494 19.125C11.2494 18.8265 11.368 18.5402 11.5791 18.3291C11.7902 18.118 12.0765 17.9994 12.375 17.9994C12.6735 17.9994 12.9598 18.118 13.1709 18.3291L15.75 20.9095L22.8291 13.8291C22.9335 13.7245 23.0576 13.6415 23.1942 13.5849C23.3308 13.5283 23.4772 13.4991 23.625 13.4991C23.7728 13.4991 23.9192 13.5283 24.0558 13.5849C24.1924 13.6415 24.3165 13.7245 24.4209 13.8291C24.5255 13.9335 24.6085 14.0576 24.6651 14.1942C24.7217 14.3308 24.7509 14.4772 24.7509 14.625C24.7509 14.7728 24.7217 14.9192 24.6651 15.0558C24.6085 15.1924 24.5255 15.3165 24.4209 15.4209Z" fill="#547C92"/>
                </svg>
              )}
              {badge.name === 'inspected' && (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0)">
                    <path d="M13.7483 0C21.305 0 27.4529 6.14798 27.4529 13.7046C27.4529 16.3405 26.7042 18.8042 25.4095 20.896L36 31.5277L31.5278 36L20.9366 25.3677C18.8454 26.6614 16.3828 27.4092 13.7483 27.4092C6.19172 27.4092 0.0437355 21.2616 0.0437355 13.7046C0.043663 6.14798 6.19137 0 13.7483 0ZM13.7485 4.2168C18.9802 4.2168 23.2363 8.47317 23.2363 13.7046C23.2363 18.9362 18.9802 23.1924 13.7485 23.1924C8.51676 23.1924 4.26074 18.9362 4.26074 13.7046C4.26074 8.47317 8.51683 4.2168 13.7485 4.2168ZM12.6933 15.3772L10.2759 12.9598L8.78516 14.4507L12.6933 18.3588L18.7096 12.3424L17.2188 10.8516L12.6933 15.3772Z" fill="#547C92"/>
                  </g>
                </svg>
              )}
              {badge.name === 'certified' && (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 1.5L4.5 6V16.5C4.5 25.5 10.5 33.75 18 36C25.5 33.75 31.5 25.5 31.5 16.5V6L18 1.5ZM15 25.5L9 19.5L11.115 17.385L15 21.255L24.885 11.37L27 13.5L15 25.5Z" fill="#547C92"/>
                </svg>
              )}
            </div>
            <div className="selected-badge-info">
              <h3>{badge.displayName}</h3>
              <span className="badge-price-tag">{formatPrice(badge.price)}/year</span>
            </div>
            <p className="badge-requirement-note">- Upload required company documents for reviewing</p>
          </div>

          <div className="documents-list">
            {documentRequirements.map((doc, index) => (
              <div key={doc.id} className={`document-item ${!doc.required ? 'optional' : ''}`}>
                <div className="document-number">{index + 1}.</div>
                <div className="document-info">
                  <span className="document-label">
                    {doc.label}
                    {!doc.required && <span className="optional-tag">(Optional)</span>}
                  </span>
                  <div className="document-actions">
                    {formData[doc.id] ? (
                      <div className="uploaded-file">
                        <span className="file-name">{formData[doc.id].name}</span>
                        <button 
                          className="remove-file-btn"
                          onClick={() => handleRemoveFile(doc.id)}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className="upload-btn">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(doc.id, e)}
                          hidden
                        />
                        <span>Upload document</span>
                      </label>
                    )}
                    <span className="status-indicator">
                      {getStatusIcon(uploadStatus[doc.id])}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="verification-modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="submit-btn" onClick={handleSubmit}>Submit</button>
        </div>
      </div>
    </div>
  );
};

export default VerificationBadgeModal;
